import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../api';
import { CalendarPanel } from '../components/CalendarPanel';
import { BRAND_META } from '../brand';
import type { BoardCard, BoardSummary, CalendarDate, Me } from '../types';

/**
 * Boards and docs, the Shell's third write exception (Matthew,
 * 14 September 2026).
 *
 * A card IS the doc: the columns show its title, opening it shows the
 * same row's body full width. There is no second object and no second
 * screen, which is what stops a note written on a card being invisible
 * to the page about it.
 *
 * Nothing is remembered in the browser. Which board you had open lives
 * in component state and resets on reload, deliberately: browser storage
 * is a hard stop in Shell artefacts, and a board that disagreed with the
 * server about where a card sat would be worse than one that reloads.
 */

interface BoardPayload {
  board: BoardSummary;
  cards: BoardCard[];
}

/** Where a card dropped between two others should sit. Sparse, so a move
 *  writes one row rather than renumbering the column. */
function positionBetween(before: BoardCard | undefined, after: BoardCard | undefined): number {
  if (!before && !after) return 0;
  if (!before) return after!.position - 1;
  if (!after) return before.position + 1;
  return (before.position + after.position) / 2;
}

export function BoardView({ api, me }: { api: ApiClient; me: Me }) {
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [slug, setSlug] = useState<string | null>(null);
  const [payload, setPayload] = useState<BoardPayload | null>(null);
  const [dates, setDates] = useState<CalendarDate[]>([]);
  // Today in London, from the server, so the panel's idea of "late" is
  // not the browser's clock in whatever timezone it happens to be in.
  const [today, setToday] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ column: string; title: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const canWrite = me.role === 'owner' || me.role === 'operator';

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<{ boards: BoardSummary[] }>('/api/boards');
        setBoards(res.boards);
        setSlug((s) => s ?? res.boards[0]?.slug ?? null);
      } catch (e) {
        // 501 means the credential is not configured, which is a
        // deployment state rather than a fault: say which.
        setUnavailable(String((e as Error).message).includes('501'));
        setError((e as Error).message);
      }
    })();
    void api
      .get<{ dates: CalendarDate[]; today: string }>('/api/calendar')
      .then((r) => {
        setDates(r.dates);
        if (r.today) setToday(r.today);
      })
      .catch(() => setDates([]));
  }, [api]);

  const load = useCallback(
    async (s: string) => {
      try {
        setPayload(await api.get<BoardPayload>(`/api/boards/${encodeURIComponent(s)}`));
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [api],
  );

  useEffect(() => {
    if (slug) void load(slug);
  }, [slug, load]);

  const byColumn = useMemo(() => {
    const map = new Map<string, BoardCard[]>();
    for (const col of payload?.board.columns ?? []) map.set(col.key, []);
    for (const card of payload?.cards ?? []) {
      const list = map.get(card.column_key);
      if (list) list.push(card);
    }
    for (const list of map.values()) list.sort((a, b) => a.position - b.position);
    return map;
  }, [payload]);

  const move = async (cardId: string, column: string, position: number) => {
    // Optimistic: a card that hangs where it was dropped for a round trip
    // feels broken. The reload underneath is what makes it true.
    setPayload((p) =>
      p
        ? { ...p, cards: p.cards.map((c) => (c.id === cardId ? { ...c, column_key: column, position } : c)) }
        : p,
    );
    try {
      await api.patch(`/api/boards/cards/${cardId}`, { column_key: column, position });
    } catch (e) {
      setError((e as Error).message);
    }
    if (slug) await load(slug);
  };

  const addCard = async (column: string, title: string, body?: string, dueOn?: string) => {
    if (!slug || !title.trim()) return;
    try {
      await api.post(`/api/boards/${encodeURIComponent(slug)}/cards`, {
        column_key: column,
        title: title.trim(),
        ...(body ? { body } : {}),
        ...(dueOn ? { due_on: dueOn } : {}),
      });
      await load(slug);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /** A date becomes a card in the first column, carrying its own brief so
   *  whoever picks it up is not sent back to the calendar to work out why. */
  const cardFromDate = (d: CalendarDate) => {
    const column = payload?.board.columns[0]?.key;
    if (!column) return;
    const body = [
      `${d.starts}${d.ends !== d.starts ? ` to ${d.ends}` : ''}`,
      `Brands: ${d.brands.join(', ')}`,
      `Preparation starts ${d.prepFrom} (${d.leadDays} days of lead).`,
      d.url ? `\n${d.url}` : '',
      d.notes ? `\n${d.notes}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    // The date goes in the due field as well as the body. Without it the
    // card knows when the work is due only in prose, so it sorts nowhere
    // and shows no date on the board - which is most of what a due date
    // is for.
    void addCard(column, d.name, body, d.starts);
  };

  if (unavailable) {
    return (
      <section className="board-view">
        <div className="card">
          <div className="card-head">
            <span className="card-title">Boards</span>
          </div>
          <p className="muted">
            Boards are not configured on this environment yet: the write credential is absent, so the
            endpoints are off rather than borrowing a wider one.
          </p>
        </div>
        <CalendarPanel dates={dates} today={today} />
      </section>
    );
  }

  /** The first board, so an empty install is one click from usable
   *  rather than a screen with nothing on it and no way forward. The
   *  columns are a default, not a rule: they are stored on the board and
   *  a later screen can change them. */
  const createFirstBoard = async () => {
    try {
      await api.post('/api/boards', {
        slug: 'work',
        title: 'Addaiaspora',
        columns: [
          { key: 'now', label: 'Now' },
          { key: 'next', label: 'Next' },
          { key: 'waiting', label: 'Waiting' },
          { key: 'done', label: 'Done' },
        ],
      });
      const res = await api.get<{ boards: BoardSummary[] }>('/api/boards');
      setBoards(res.boards);
      setSlug(res.boards[0]?.slug ?? null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const card = payload?.cards.find((c) => c.id === openCard) ?? null;

  return (
    <section className="board-view">
      {error && !unavailable && <p className="error">{error}</p>}

      <CalendarPanel dates={dates} today={today} onMakeCard={canWrite && payload ? cardFromDate : undefined} />

      {boards.length > 1 && (
        <div className="board-picker" role="tablist">
          {boards.map((b) => (
            <button
              key={b.slug}
              role="tab"
              aria-selected={b.slug === slug}
              className="brand-tab"
              style={{
                ['--tab-accent' as string]: b.brand ? (BRAND_META[b.brand]?.series ?? 'var(--ink)') : 'var(--ink)',
              }}
              onClick={() => {
                setSlug(b.slug);
                setOpenCard(null);
              }}
            >
              {b.title}
              {b.open_cards > 0 && <span className="badge">{b.open_cards}</span>}
            </button>
          ))}
        </div>
      )}

      {boards.length === 0 && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">No board yet</span>
          </div>
          <p className="muted">
            {canWrite
              ? 'Start with Now, Next, Waiting and Done. The columns are stored on the board, so they can change later.'
              : 'Nobody has created a board yet. An owner or operator can start one.'}
          </p>
          {canWrite && (
            <button type="button" onClick={() => void createFirstBoard()}>
              Create the first board
            </button>
          )}
        </div>
      )}

      {payload && (
        <div className="board-columns">
          {payload.board.columns.map((col) => {
            const cards = byColumn.get(col.key) ?? [];
            return (
              <div
                key={col.key}
                className="board-column"
                onDragOver={(e) => canWrite && e.preventDefault()}
                onDrop={(e) => {
                  if (!canWrite) return;
                  e.preventDefault();
                  const id = e.dataTransfer.getData('text/card-id');
                  if (id) void move(id, col.key, positionBetween(cards[cards.length - 1], undefined));
                }}
              >
                <div className="board-column-head">
                  <span className="card-title">{col.label}</span>
                  <span className="card-note">{cards.length}</span>
                </div>

                {cards.map((c, i) => (
                  <article
                    key={c.id}
                    className={c.id === openCard ? 'board-card open' : 'board-card'}
                    draggable={canWrite}
                    onDragStart={(e) => e.dataTransfer.setData('text/card-id', c.id)}
                    onDragOver={(e) => canWrite && e.preventDefault()}
                    onDrop={(e) => {
                      if (!canWrite) return;
                      e.preventDefault();
                      e.stopPropagation();
                      const id = e.dataTransfer.getData('text/card-id');
                      if (id && id !== c.id) void move(id, col.key, positionBetween(cards[i - 1], c));
                    }}
                  >
                    <button type="button" className="board-card-open" onClick={() => setOpenCard(c.id)}>
                      {c.title}
                    </button>
                    <div className="board-card-meta">
                      {c.due_on && <span className="board-due">{c.due_on}</span>}
                      {c.assignee && <span className="board-assignee">{c.assignee.split('@')[0]}</span>}
                      {c.body.trim() !== '' && (
                        <span className="board-hasdoc" title="Has a document">
                          doc
                        </span>
                      )}
                    </div>
                  </article>
                ))}

                {canWrite &&
                  (draft?.column === col.key ? (
                    <form
                      className="board-add"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void addCard(col.key, draft.title);
                        setDraft(null);
                      }}
                    >
                      <input
                        autoFocus
                        value={draft.title}
                        placeholder="Card title"
                        onChange={(e) => setDraft({ column: col.key, title: e.target.value })}
                        onBlur={() => draft.title.trim() === '' && setDraft(null)}
                      />
                      <button type="submit">Add</button>
                    </form>
                  ) : (
                    <button type="button" className="ghost board-add-open" onClick={() => setDraft({ column: col.key, title: '' })}>
                      Add a card
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}

      {card && <CardDoc api={api} card={card} canWrite={canWrite} onDone={() => slug && void load(slug)} onClose={() => setOpenCard(null)} />}
    </section>
  );
}

/**
 * The card, full width, as a document.
 *
 * Same row as the board card. The body is stored as markdown and shown
 * as written: rendering it would need a parser, and a parser that runs
 * on text people paste in is a decision to make deliberately rather than
 * as a detail of a board.
 */
function CardDoc({
  api,
  card,
  canWrite,
  onDone,
  onClose,
}: {
  api: ApiClient;
  card: BoardCard;
  canWrite: boolean;
  onDone: () => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [body, setBody] = useState(card.body);
  const [assignee, setAssignee] = useState(card.assignee ?? '');
  const [due, setDue] = useState(card.due_on ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // A different card in the same panel must not keep the last one's text.
  useEffect(() => {
    setTitle(card.title);
    setBody(card.body);
    setAssignee(card.assignee ?? '');
    setDue(card.due_on ?? '');
    setSaved(false);
  }, [card.id, card.title, card.body, card.assignee, card.due_on]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/boards/cards/${card.id}`, {
        title,
        body,
        assignee: assignee.trim() === '' ? null : assignee.trim(),
        due_on: due === '' ? null : due,
      });
      setSaved(true);
      onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card board-doc">
      <div className="card-head">
        <span className="card-title">{canWrite ? 'Card' : 'Card (read only)'}</span>
        <span className="card-note">
          {card.updated_by ? `edited by ${card.updated_by}` : `added by ${card.created_by}`}
        </span>
      </div>

      <input className="board-doc-title" value={title} disabled={!canWrite} onChange={(e) => setTitle(e.target.value)} />

      <div className="board-doc-fields">
        <label>
          Assignee
          <input value={assignee} disabled={!canWrite} placeholder="name@addaiaspora.com" onChange={(e) => setAssignee(e.target.value)} />
        </label>
        <label>
          Due
          <input type="date" value={due} disabled={!canWrite} onChange={(e) => setDue(e.target.value)} />
        </label>
      </div>

      <textarea
        className="board-doc-body"
        value={body}
        disabled={!canWrite}
        rows={16}
        placeholder="Write the brief here. Markdown is kept as typed."
        onChange={(e) => setBody(e.target.value)}
      />

      <div className="board-doc-actions">
        {canWrite && (
          <button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              void api.post(`/api/boards/cards/${card.id}/archive`, {}).then(() => {
                onClose();
                onDone();
              });
            }}
          >
            Archive
          </button>
        )}
        <button type="button" className="ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
